// `updateMasterSequence` on the move call carries the master side — no separate master step.
import { buildRouteSlotMoveRuns } from 'utils/routeStopOperations/v2/slotPayloads'
import { makeMoveRouteSlotStep } from 'utils/routeStopOperations/v2/steps/moveRouteSlot'
import { makePreflightStep } from 'utils/routeStopOperations/v2/steps/preflight'
import { makeVerifyStep } from 'utils/routeStopOperations/v2/steps/verify'

import type { Deps, MoveIntent, OperationPlan, PipelineStep } from 'types/modules/routing'

const STRATEGY_ID = 'v3-slot' as const

export const buildMoveSlotPipeline = (intent: MoveIntent, deps: Deps): OperationPlan => {
  const steps: PipelineStep[] = [makePreflightStep({ intent }, deps) as PipelineStep]
  const updateMasterSequence = intent.includeMaster === true
  const movedSlotIds = new Set(intent.stops.map(stop => stop.routeSlotId).filter(Boolean))

  // No destination order means no anchor to read, so everything goes in one call at the top.
  const runs =
    intent.destinationOrderedStops && intent.destinationOrderedStops.length > 0
      ? buildRouteSlotMoveRuns(intent.destinationOrderedStops, row => movedSlotIds.has(row.routeSlotId), intent.serviceDate)
      : [
          {
            targetRouteSlotId: null,
            slots: intent.stops
              .filter(stop => stop.routeSlotId)
              .map(stop => ({ routeSlotId: stop.routeSlotId as string, serviceDate: intent.serviceDate })),
          },
        ]

  runs.forEach((run, runIndex) => {
    steps.push(
      makeMoveRouteSlotStep(
        {
          routeId: intent.routeId,
          serviceDate: intent.serviceDate,
          slots: run.slots,
          targetRouteSlotId: run.targetRouteSlotId,
          updateMasterSequence,
          runIndex,
        },
        deps
      ) as PipelineStep
    )
  })

  steps.push(
    makeVerifyStep({ intent, expectedAssignedWorkOrderIds: intent.stops.map(stop => String(stop.workOrderId)) }, deps) as PipelineStep
  )

  return { strategy: STRATEGY_ID, intent, steps }
}
